import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import managerLessonApi from "api/managerLesson";
import { useAppDispatch, useAppSelector } from "app/hooks";
import BG_LESSON_STUDENT from "assets/img/bg_detail_lesson_student.png";
import LOADING from "assets/img/loading.gif";
import clsx from "clsx";
import ControlVideo from "components/ControlVideo";
import { BASE_URL } from "constants/config";
import { ResponseStatus } from "models/common";
import { Comment, LessonByUser, ListResponseComments } from "models/managerLesson";
import queryString from "query-string";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchListLessonByUser, selectFilterLessonListByUser } from "../ListLesson/listLessonSlice";
import CreateComment from "./components/CreateComment";
import ListComment from "./components/ListComment";
import ListVideoRecommend from "./components/ListVideoRecommend";
import ListCommentMobile from "./components/Mobile/ListCommentMobile";
import ListVideoRecommandMobile from "./components/Mobile/ListVideoRecommendMobile";
import {
  fetchDetailLesson,
  selectDetailLesson,
  selectLoadingDetailLesson,
} from "./detailLessonSlice";

let count = 0;

export default function DetailLesson() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 24,
      paddingBottom: 48,
      maxWidth: 1400,
      margin: "0 auto",
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 16,
        paddingBottom: 24,
        paddingLeft: 12,
        paddingRight: 12,
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    playerFrame: {
      borderRadius: 24,
      overflow: "hidden",
      backgroundColor: "#0F172A",
      boxShadow: "0 16px 40px rgba(15, 23, 42, 0.3)",
      marginBottom: 24,
      position: "relative",
    },
    playerWrapper: {
      position: "relative",
      paddingTop: "56.25%", /* 16:9 Aspect Ratio */
    },
    reactPlayer: {
      position: "absolute",
      top: 0,
      left: 0,
    },
    playIconBtn: {
      fontSize: 64,
      color: "#FFFFFF",
      backgroundColor: "#4F46E5",
      borderRadius: "50%",
      padding: 12,
      boxShadow: "0 8px 24px rgba(79, 70, 229, 0.5)",
      transition: "transform 0.2s ease",
      "&:hover": {
        transform: "scale(1.1)",
      },
    },

    // Lesson Metadata Card
    infoCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 28,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      marginBottom: 24,
      [theme.breakpoints?.down("sm")]: {
        padding: 18,
        borderRadius: 16,
      },
    },
    categoryChip: {
      backgroundColor: "#EEF2FF",
      color: "#4F46E5",
      fontWeight: 900,
      fontSize: "0.775rem",
      padding: "4px 12px",
      borderRadius: 8,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    lessonTitle: {
      fontSize: "1.65rem",
      fontWeight: 900,
      color: "#0F172A",
      lineHeight: 1.35,
      marginTop: 12,
      marginBottom: 12,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.2rem",
      },
    },
    metaRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      paddingBottom: 16,
      marginBottom: 16,
      borderBottom: "1px solid #F1F5F9",
    },
    dateText: {
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "#64748B",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    descTitle: {
      fontSize: "0.95rem",
      fontWeight: 800,
      color: "#0F172A",
      marginBottom: 6,
    },
    descContent: {
      fontSize: "0.925rem",
      color: "#475569",
      lineHeight: 1.6,
    },

    // Comments Section
    commentSection: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 28,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      [theme.breakpoints?.down("sm")]: {
        padding: 18,
      },
    },
    showMoreBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      color: "#4F46E5",
      fontWeight: 800,
      cursor: "pointer",
      padding: "10px 0",
      transition: "color 0.2s ease",
      "&:hover": {
        color: "#3730A3",
      },
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 400,
    },
  }));

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(true);
  const detailLesson = useAppSelector(selectDetailLesson);
  const loading = useAppSelector(selectLoadingDetailLesson);
  const params = useParams();
  const location = useLocation();
  const querySearch: any = queryString.parse(location.search);
  const dispatch = useAppDispatch();
  const filterListLesson = useAppSelector(selectFilterLessonListByUser);

  const [listComment, setListComment] = useState<ListResponseComments<Comment> | undefined>();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filter, setFilter] = useState({ page: 0, size: 3 });

  const playerWrapperRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const controlsRef = useRef<any>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [detailLesson?.lessionUsers?.lession?.attachmentID]);

  const handleFetchListComment = useCallback(async () => {
    try {
      const res: ListResponseComments<Comment> = await managerLessonApi.getComments({
        page: 0,
        size: 3,
        lessionId: Number(params.id),
      });
      setListComment(res);
    } catch (error) {}
  }, [params]);

  useEffect(() => {
    dispatch(
      fetchListLessonByUser({
        ...filterListLesson,
        size: 20,
        month: querySearch.month,
        year: querySearch.year,
      })
    );
  }, [detailLesson]);

  useEffect(() => {
    handleFetchListComment();
    setFilter({ page: 0, size: 3 });
  }, [params]);

  useEffect(() => {
    dispatch(
      fetchDetailLesson({
        id: Number(params?.id),
        loading: true,
      })
    );
  }, [params]);

  const classes = useStyles();

  const handleShowMore = async () => {
    setFilter({ ...filter, page: filter.page + 1 });
    try {
      const res: ListResponseComments<Comment> = await managerLessonApi.getComments({
        ...filter,
        lessionId: Number(params.id),
        page: filter.page + 1,
      });
      const dataListCommentNext = res.comments.content;
      const currentListComment = { ...res };
      const newListComment = listComment?.comments?.content?.concat(dataListCommentNext);
      currentListComment.comments.content = newListComment;
      setListComment(currentListComment);
    } catch (error) {}
  };

  //  Control Video
  const [showControl, setShowControl] = useState(false);
  const [control, setControl] = useState({
    played: 0,
    muted: false,
    volume: 1,
    playbackRate: 1.0,
    playing: true,
    start: false,
  });

  useEffect(() => {
    setControl({
      played: 0,
      muted: false,
      volume: 1,
      playbackRate: 1.0,
      playing: true,
      start: false,
    });
    setShowControl(false);
    if (detailLesson?.lessionUsers?.lastSeenTime > 0 && playerRef.current) {
      playerRef.current.seekTo(detailLesson?.lessionUsers?.lastSeenTime, "seconds");
    }
  }, [detailLesson]);

  const currentTime = playerRef && playerRef.current ? playerRef.current.getCurrentTime() : "00:00";
  const duration = playerRef && playerRef.current ? playerRef.current.getDuration() : "00:00";

  useEffect(() => {
    if (detailLesson?.lessionUsers?.watched) return;
    const handleWatched = async (detail: LessonByUser | undefined) => {
      try {
        const res: ResponseStatus = await managerLessonApi.postWatchedStatus({
          lessionId: detail?.lessionUsers?.lessionId,
          isWatched: !detail?.lessionUsers?.watched,
        });
        if (res.responseCode === "00") {
          dispatch(
            fetchDetailLesson({
              id: Number(params?.id),
              loading: false,
            })
          );
        }
      } catch (error) {}
    };
    if (currentTime / duration > 0.98) {
      handleWatched(detailLesson);
    }
  }, [currentTime, detailLesson]);

  const handleToggleScreenfull = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (playerWrapperRef.current?.requestFullscreen) {
      playerWrapperRef.current.requestFullscreen();
    }
  };

  const handleMouseMove = () => {
    if (!controlsRef.current) return;
    controlsRef.current.style.visibility = "visible";
    count = 0;
  };

  const hanldeMouseLeave = () => {
    if (!controlsRef.current) return;
    controlsRef.current.style.visibility = "hidden";
    count = 0;
  };

  const handleProgress = (changeState: any) => {
    if (!controlsRef.current) return;
    if (count > 2) {
      controlsRef.current.style.visibility = "hidden";
      count = 0;
    }
    if (controlsRef.current.style.visibility === "visible") {
      count += 1;
    }
    setControl({ ...control, played: changeState.played });
  };

  useEffect(() => {
    if (control.start) {
      const timer = setInterval(async () => {
        if (playerRef.current && detailLesson?.lessionUsers?.id) {
          await managerLessonApi.trackLessonLastSeen({
            lessonUserID: detailLesson.lessionUsers.id,
            lastSeenTime: playerRef.current.getCurrentTime(),
          });
        }
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [control.start, detailLesson]);

  const lessonObj = detailLesson?.lessionUsers?.lession;
  const isWatched = detailLesson?.lessionUsers?.watched ?? false;
  const pubDateStr = String(lessonObj?.publishDate || "20260805");
  const day = pubDateStr.slice(-2);
  const month = pubDateStr.slice(4, 6);
  const year = pubDateStr.slice(0, 4);

  const videoSourceUrl =
    lessonObj?.attachment?.url ||
    (lessonObj?.attachmentID
      ? isMobile
        ? lessonObj?.attachment?.url
        : `${BASE_URL}/common/video/${lessonObj.attachmentID}`
      : "");

  return (
    <Container className={classes.container} maxWidth={false}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        className={classes.backButton}
        onClick={() => navigate(-1)}
      >
        Quay lại danh sách bài học
      </Button>

      <Grid container spacing={3}>
        {/* Left / Main Column: Video Player & Info */}
        <Grid item xs={12} lg={openDrawer ? 8 : 10}>
          {loading ? (
            <Box className={classes.loading}>
              <img src={LOADING} alt="LOADING" />
            </Box>
          ) : (
            <>
              {/* Sleek Player Frame */}
              <Paper className={classes.playerFrame} elevation={0}>
                <div
                  className={classes.playerWrapper}
                  ref={playerWrapperRef}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={hanldeMouseLeave}
                  onClick={() => setShowControl(true)}
                >
                  <ReactPlayer
                    onEnded={() => setControl({ ...control, playing: false })}
                    playsinline
                    playing={control.playing}
                    className={classes.reactPlayer}
                    url={videoSourceUrl}
                    controls={false}
                    width="100%"
                    height="100%"
                    light={lessonObj?.attachment?.urlThumbnail}
                    playIcon={
                      <PlayArrowIcon
                        className={classes.playIconBtn}
                        onClick={() => setShowControl(true)}
                      />
                    }
                    ref={playerRef}
                    config={{ file: { attributes: { controlsList: "nodownload" } } }}
                    onContextMenu={(e: any) => e.preventDefault()}
                    muted={control.muted}
                    volume={control.volume}
                    playbackRate={control.playbackRate}
                    onProgress={handleProgress}
                    onStart={() => {
                      setControl({ ...control, start: !control.start });
                      const timer = detailLesson?.lessionUsers?.lastSeenTime;
                      if (timer && playerRef.current) {
                        playerRef.current.seekTo(timer, "seconds");
                      }
                    }}
                  />

                  {showControl && (
                    <ControlVideo
                      isWatched={isWatched}
                      playerRef={playerRef}
                      handleToggleScreenfull={handleToggleScreenfull}
                      ref={controlsRef}
                      setControl={setControl}
                      control={control}
                      currentTime={currentTime}
                      duration={duration}
                    />
                  )}
                </div>
              </Paper>

              {/* Lesson Details Card */}
              <Paper className={classes.infoCard} elevation={0}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box className={classes.categoryChip}>
                    {lessonObj?.lessionCategory?.name || "BÀI HỌC CHUYÊN ĐỀ"}
                  </Box>

                  {isWatched ? (
                    <Chip
                      icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
                      label={t("home.detaillesson.daxem")}
                      size="small"
                      style={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800 }}
                    />
                  ) : (
                    <Chip
                      icon={<VisibilityIcon style={{ color: "#4F46E5" }} />}
                      label={t("home.detaillesson.chuaxem")}
                      size="small"
                      style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 800 }}
                    />
                  )}
                </Box>

                <Typography className={classes.lessonTitle}>
                  {lessonObj?.lessionName}
                </Typography>

                <Box className={classes.metaRow}>
                  <Typography className={classes.dateText}>
                    <CalendarTodayIcon style={{ fontSize: 16, color: "#64748B" }} />
                    Ngày phát hành: {day}/{month}/{year}
                  </Typography>
                </Box>

                <Typography className={classes.descTitle}>
                  {t("home.detaillesson.mota")}:
                </Typography>
                <Typography className={classes.descContent}>
                  {lessonObj?.lessionDescription}
                </Typography>
              </Paper>

              {/* Comments Section */}
              <Paper className={classes.commentSection} elevation={0}>
                {isTablet ? (
                  <ListCommentMobile />
                ) : (
                  <>
                    <Typography variant="h6" style={{ fontWeight: 800, color: "#0F172A", marginBottom: 16 }}>
                      {listComment?.comments?.totalElements || 14} {t("home.detaillesson.binhluan")}
                    </Typography>

                    <CreateComment handleFetchListComment={handleFetchListComment} />

                    {listComment?.comments?.content?.map((comment: Comment, index: number) => (
                      <Fragment key={index}>
                        <ListComment
                          userComment={comment}
                          handleFetchListComment={handleFetchListComment}
                        />
                      </Fragment>
                    ))}

                    {listComment?.comments?.number + 1 !== listComment?.comments?.totalPages &&
                    listComment?.comments?.content &&
                    listComment?.comments?.content?.length > 0 ? (
                      <Box className={classes.showMoreBtn} onClick={handleShowMore} mt={3}>
                        <ArrowDropDownIcon />
                        <Typography variant="subtitle2">
                          {t("home.detaillesson.hienthithembinhluan")}
                        </Typography>
                      </Box>
                    ) : null}
                  </>
                )}
              </Paper>
            </>
          )}
        </Grid>

        {/* Right Column: Recommendations */}
        <Grid item xs={12} lg={openDrawer ? 4 : 2}>
          {isTablet ? (
            <ListVideoRecommandMobile />
          ) : (
            <>
              <ListVideoRecommend openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} />
              <Paper
                elevation={0}
                style={{
                  marginTop: 20,
                  borderRadius: 20,
                  padding: 24,
                  background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.2)",
                }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1.5}>
                  <EmojiObjectsIcon style={{ color: "#FDE047", fontSize: 20 }} />
                  <Typography style={{ fontWeight: 900, fontSize: "0.95rem", color: "#FDE047", letterSpacing: "0.02em" }}>
                    MẸO HỌC TẬP HIỆU QUẢ
                  </Typography>
                </Box>

                <Typography style={{ fontSize: "0.85rem", color: "#CBD5E1", lineHeight: 1.6, marginBottom: 16 }}>
                  Theo dõi đầy đủ video bài giảng và tham gia thảo luận bên dưới để hoàn thành chỉ tiêu học tập GPET Edu tháng này!
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#4F46E5",
                    color: "#FFFFFF",
                    fontWeight: 800,
                    borderRadius: 12,
                    textTransform: "none",
                    fontSize: "0.825rem",
                    padding: "10px 16px",
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.4)",
                  }}
                  onClick={() => navigate("/academic-record")}
                >
                  Xem hồ sơ học tập
                </Button>
              </Paper>
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

